import { Controller } from "@hotwired/stimulus"

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)"

// Drives the Aquifer UI sidebar:
//  - desktop collapse/expand (persisted to localStorage)
//  - expandable submenus (inline when expanded; flyout when collapsed or program picker)
//  - mobile off-canvas drawer with backdrop + Escape support
//
// Flyout mechanics are composed from the standalone `aquifer-ui-flyout`
// controller via Stimulus outlets: the sidebar locates the outlet for a
// trigger, configures it (detach/placement/root/class names) at click time,
// then drives it. Open/close notifications flow back as `aquifer-ui-flyout:open`
// / `:close` events wired to #flyoutOpened / #flyoutClosed on the nav element.
export default class extends Controller {
  static targets = ["panel", "collapseToggle"]
  static outlets = ["aquifer-ui-flyout"]
  static values = {
    collapsed: Boolean,
    storageKey: { type: String, default: "aquifer-ui-sidebar-collapsed" },
  }

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleOutsidePointer = this.handleOutsidePointer.bind(this)
    this.element.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("mousedown", this.handleOutsidePointer)

    const stored = this.readStoredCollapsed()
    this.collapsed = stored === null ? this.collapsedValue : stored
    this.applyCollapsed()
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("mousedown", this.handleOutsidePointer)
    this.closeAllFlyouts()
  }

  // --- Collapse / expand (desktop) -----------------------------------------
  toggleCollapsed(event) {
    event?.preventDefault()
    this.collapsed = !this.collapsed
    this.applyCollapsed()
    this.writeStoredCollapsed(this.collapsed)
  }

  applyCollapsed() {
    this.element.classList.toggle("aqui-sidebar--collapsed", this.collapsed)
    this.closeAllFlyouts()
    if (this.collapsed) {
      this.closeAllInlineSubmenus()
    }

    if (this.hasCollapseToggleTarget) {
      this.collapseToggleTarget.setAttribute(
        "aria-label",
        this.collapsed ? "Expand navigation" : "Collapse navigation"
      )
      this.collapseToggleTarget.setAttribute("aria-expanded", String(!this.collapsed))
    }
  }

  // --- Submenus / program list ---------------------------------------------
  toggleSubmenu(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    const controlsId = trigger.getAttribute("aria-controls")
    const panel = controlsId ? document.getElementById(controlsId) : null
    if (!panel) return

    const programPicker = this.isProgramPanel(panel)

    if (this.isDesktopViewport() && (this.isCollapsed() || programPicker)) {
      if (programPicker) {
        this.closeAllInlineSubmenus()
      }

      const flyout = this.flyoutOutletFor(trigger)
      if (flyout) {
        flyout.configure({
          detach: this.isCollapsed() && !programPicker,
          placement: "right-start",
          root: this.panelTarget,
          container: flyout.element,
          openClass: "is-flyout-open",
          detachedClass: "aqui-sidebar__flyout--detached",
          closeOnEscape: false,
          closeOnScroll: false,
        })
        flyout.toggle()
      }
      return
    }

    this.closeAllFlyouts()
    this.toggleInlineSubmenu(trigger, panel)
  }

  isProgramPanel(panel) {
    return panel.classList.contains("aqui-sidebar__program-list")
  }

  toggleInlineSubmenu(trigger, panel) {
    const willOpen = trigger.getAttribute("aria-expanded") !== "true"
    trigger.setAttribute("aria-expanded", String(willOpen))
    panel.hidden = !willOpen

    const item = trigger.closest(".aqui-sidebar__item")
    if (item) {
      item.classList.toggle("is-open", willOpen)
    }
  }

  // Finds the aquifer-ui-flyout outlet controller hosting the given trigger.
  flyoutOutletFor(trigger) {
    const host = trigger.closest("[data-controller~='aquifer-ui-flyout']")
    if (!host) return null

    return this.aquiferUiFlyoutOutlets.find((flyout) => flyout.element === host) || null
  }

  closeAllFlyouts() {
    if (!this.hasAquiferUiFlyoutOutlet) return

    this.aquiferUiFlyoutOutlets.forEach((flyout) => flyout.close())
  }

  // --- Flyout open/close notifications --------------------------------------
  // Mirror the flyout's open state onto the owning menu item so the chevron
  // rotates (matches the inline submenu's `is-open` behavior).
  flyoutOpened(event) {
    this.markItemOpen(event.target, true)
  }

  flyoutClosed(event) {
    this.markItemOpen(event.target, false)
  }

  markItemOpen(host, open) {
    const item = host?.closest(".aqui-sidebar__item")
    if (item) {
      item.classList.toggle("is-open", open)
    }
  }

  closeAllInlineSubmenus() {
    this.element
      .querySelectorAll(".aqui-sidebar__link[aria-expanded='true'], .aqui-sidebar__program[aria-expanded='true']")
      .forEach((trigger) => {
        const controlsId = trigger.getAttribute("aria-controls")
        const panel = controlsId ? document.getElementById(controlsId) : null
        trigger.setAttribute("aria-expanded", "false")
        if (panel) {
          panel.hidden = true
        }
        const item = trigger.closest(".aqui-sidebar__item")
        if (item) {
          item.classList.remove("is-open")
        }
      })
  }

  // --- Selection ------------------------------------------------------------
  // Demo affordance: marks the clicked menu/submenu entry as the single active
  // item. Real surfaces drive the active state server-side, so this is only
  // wired where placeholder links need client-side selection.
  select(event) {
    event.preventDefault()
    const target = event.currentTarget

    this.element
      .querySelectorAll(".aqui-sidebar__link, .aqui-sidebar__sublink")
      .forEach((el) => {
        el.classList.remove("is-active")
        el.removeAttribute("aria-current")
      })

    target.classList.add("is-active")
    target.setAttribute("aria-current", "page")

    // A submenu header stays selected while one of its subitems is active.
    const submenu = target.closest(".aqui-sidebar__submenu")
    if (submenu) {
      this.element
        .querySelector(`[aria-controls="${submenu.id}"]`)
        ?.classList.add("is-active")
    }

    this.closeAllFlyouts()
  }

  // --- Mobile drawer --------------------------------------------------------
  toggleDrawer(event) {
    if (this.element.classList.contains("is-open")) {
      this.closeDrawer(event)
    } else {
      this.openDrawer(event)
    }
  }

  openDrawer(event) {
    event?.preventDefault()
    this.setDrawerOpen(true)
    requestAnimationFrame(() => {
      const firstLink = this.hasPanelTarget
        ? this.panelTarget.querySelector("a, button")
        : null
      firstLink?.focus()
    })
  }

  closeDrawer(event) {
    event?.preventDefault()
    this.setDrawerOpen(false)
    this.drawerToggle?.focus()
  }

  setDrawerOpen(open) {
    this.element.classList.toggle("is-open", open)
    if (this.drawerToggle) {
      this.drawerToggle.setAttribute("aria-expanded", String(open))
      this.drawerToggle.setAttribute(
        "aria-label",
        open ? "Close navigation menu" : "Open navigation menu"
      )
    }
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return

    if (this.element.querySelector(".is-flyout-open")) {
      event.preventDefault()
      this.closeAllFlyouts()
      return
    }

    // Inline submenus (e.g. the program picker on mobile/tablet, where it opens
    // in-flow rather than as a flyout) close on Escape, returning focus to the
    // trigger before falling through to closing the mobile drawer.
    const openTrigger = this.openInlineSubmenuTrigger()
    if (openTrigger) {
      event.preventDefault()
      this.closeAllInlineSubmenus()
      openTrigger.focus()
      return
    }

    if (this.element.classList.contains("is-open")) {
      this.closeDrawer(event)
    }
  }

  openInlineSubmenuTrigger() {
    return this.element.querySelector(
      ".aqui-sidebar__link[aria-expanded='true'], .aqui-sidebar__program[aria-expanded='true']"
    )
  }

  // A pointer landing anywhere outside the sidebar dismisses open surfaces: the
  // inline program picker on mobile/tablet and the mobile off-canvas drawer.
  // Desktop flyouts manage their own outside-dismiss, so bail out (and avoid
  // treating an open flyout's aria-expanded trigger as an inline submenu).
  handleOutsidePointer(event) {
    if (this.element.contains(event.target)) return
    if (this.element.querySelector(".is-flyout-open")) return

    this.closeAllInlineSubmenus()

    if (this.element.classList.contains("is-open")) {
      this.closeDrawer()
    }
  }

  isCollapsed() {
    return this.element.classList.contains("aqui-sidebar--collapsed")
  }

  isDesktopViewport() {
    if (typeof window.matchMedia !== "function") return false

    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches
  }

  get drawerToggle() {
    return this.element.querySelector(".aqui-sidebar__mobile-toggle")
  }

  readStoredCollapsed() {
    try {
      const raw = window.localStorage.getItem(this.storageKeyValue)
      if (raw === null) return null
      return raw === "true"
    } catch (_error) {
      return null
    }
  }

  writeStoredCollapsed(value) {
    try {
      window.localStorage.setItem(this.storageKeyValue, String(value))
    } catch (_error) {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }
}
